import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card, Field, Input, Modal, Select, Spinner } from "@aigntx/ui";
import type { BadgeTone } from "@aigntx/ui";
import { endpoints } from "../api";
import { useAuth } from "../auth";
import type { Catalog, Project } from "../types";

export function human(s: string): string {
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export function statusTone(status: string): BadgeTone {
  if (status === "construction") return "accent";
  if (status === "handover" || status === "closed") return "good";
  if (status === "on_hold") return "warn";
  return "neutral";
}

export function ProjectsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const navigate = useNavigate();
  const canManage = can("projects.manage");
  const [rows, setRows] = useState<Project[] | null>(null);
  const [catalog, setCatalog] = useState<Catalog | null>(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    Promise.all([endpoints.listProjects(), endpoints.getCatalog()])
      .then(([p, c]) => {
        setRows(p);
        setCatalog(c);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error)
    return (
      <div className="page">
        <Card>{error}</Card>
      </div>
    );
  if (!rows || !catalog)
    return (
      <div className="page center">
        <Spinner />
      </div>
    );

  return (
    <div className="page">
      <header className="page__head between">
        <div>
          <h1 className="page__title">{t("projects.title")}</h1>
          <p className="page__sub">{t("projects.subtitle")}</p>
        </div>
        {canManage && <Button onClick={() => setOpen(true)}>{t("projects.new")}</Button>}
      </header>

      {rows.length === 0 ? (
        <Card>
          <p className="muted">{t("projects.empty")}</p>
        </Card>
      ) : (
        <div className="proj-grid">
          {rows.map((p) => (
            <Link to={`/projects/${p.id}`} key={p.id} className="proj-card">
              <div className="proj-card__top">
                <span className="proj-card__code mono">{p.code || "—"}</span>
                <Badge tone={statusTone(p.status)}>{human(p.status)}</Badge>
              </div>
              <div className="proj-card__name">{p.name}</div>
              <div className="proj-card__client">{p.clientName || "—"}</div>
              <div className="proj-card__meta">
                <span>{human(p.sector)}</span>
                <span className="mono">
                  {p.stakeholderCount} ◦ {p.phaseCount}
                </span>
              </div>
            </Link>
          ))}
        </div>
      )}

      {open && (
        <ProjectModal
          catalog={catalog}
          onClose={() => setOpen(false)}
          onCreated={(p) => navigate(`/projects/${p.id}`)}
        />
      )}
    </div>
  );
}

function ProjectModal({
  catalog,
  onClose,
  onCreated,
}: {
  catalog: Catalog;
  onClose: () => void;
  onCreated: (p: Project) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [clientName, setClientName] = useState("");
  const [location, setLocation] = useState("");
  const [sector, setSector] = useState(catalog.projectSectors[0] ?? "building");
  const [status, setStatus] = useState(catalog.projectStatuses[0] ?? "planning");
  const [startDate, setStartDate] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit() {
    setBusy(true);
    setError("");
    try {
      onCreated(
        await endpoints.createProject({
          name,
          code,
          clientName,
          location,
          sector,
          status,
          startDate,
          targetDate,
        }),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  return (
    <Modal
      open
      title={t("projects.new")}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button loading={busy} onClick={() => void submit()}>
            {t("projects.createSetup")}
          </Button>
        </>
      }
    >
      {error && <div className="form-err">{error}</div>}
      <p className="page__sub">{t("projects.setupHint")}</p>
      <Field label={t("projects.name")}>
        <Input value={name} onChange={(e) => setName(e.target.value)} />
      </Field>
      <div className="grid2-form">
        <Field label={t("projects.code")}>
          <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="PRJ-001" />
        </Field>
        <Field label={t("projects.client")}>
          <Input value={clientName} onChange={(e) => setClientName(e.target.value)} />
        </Field>
      </div>
      <Field label={t("projects.location")}>
        <Input value={location} onChange={(e) => setLocation(e.target.value)} />
      </Field>
      <div className="grid2-form">
        <Field label={t("projects.sector")}>
          <Select value={sector} onChange={(e) => setSector(e.target.value)}>
            {catalog.projectSectors.map((s) => (
              <option key={s} value={s}>
                {human(s)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label={t("projects.status")}>
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            {catalog.projectStatuses.map((s) => (
              <option key={s} value={s}>
                {human(s)}
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <div className="grid2-form">
        <Field label={t("projects.startDate")}>
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label={t("projects.targetDate")}>
          <Input type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}
